import {
  HTTP_INTERCEPTORS,
  HttpContext,
  HttpContextToken,
  HttpEvent,
  HttpHandlerFn,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { LoadingService } from '@core/services/loading/loading.service';
import { environment } from '@env';
import { from, Observable } from 'rxjs';
import { finalize, switchMap } from 'rxjs/operators';

export const DISABLE_LOADING = new HttpContextToken<boolean>(() => false);
export const DISABLE_AUTH_HEADERS = new HttpContextToken<boolean>(() => false);

export const disableLoading = () => new HttpContext().set(DISABLE_LOADING, true);
export const disableAuthHeaders = () => new HttpContext().set(DISABLE_AUTH_HEADERS, true);

/**
 * Check if a JWT token is expired or will expire within the given buffer time (in seconds)
 */
function isTokenExpired(token: string, bufferSeconds = 300): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const expirationTime = payload.exp * 1000; // Convert to milliseconds
    const currentTime = Date.now();
    const bufferTime = bufferSeconds * 1000;
    return currentTime >= expirationTime - bufferTime;
  } catch {
    return true; // If we can't parse the token, consider it expired
  }
}

function hasExpectedAudience(token: string, expectedAudiences: string[]): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const aud = payload?.aud;
    if (!aud || typeof aud !== 'string') {
      return false;
    }

    return expectedAudiences.includes(aud);
  } catch {
    return false;
  }
}

export function HttpRequestInterceptor(req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const loading = inject(LoadingService);
  const msalService = inject(MsalService);
  const isMartechRequest = req.url.startsWith(environment.api_root_martech);
  const tokenStorageKey = isMartechRequest ? 'pcoAccessToken' : 'graphAccessToken';
  const tokenScopes = isMartechRequest ? ['api://' + environment.clientId + '/Validation'] : ['User.Read'];
  const expectedAudiences =
    isMartechRequest && !!environment.clientId ? [environment.clientId, 'api://' + environment.clientId] : null;

  if (!req.context.get(DISABLE_LOADING)) loading.set(true, req.url);

  if (req.context.get(DISABLE_AUTH_HEADERS)) {
    return next(req).pipe(
      finalize(() => {
        loading.set(false, req.url);
      })
    );
  }

  const account = msalService.instance.getActiveAccount();
  const accessToken = window.localStorage.getItem(tokenStorageKey);
  const hasValidAudience =
    !isMartechRequest || !accessToken || !expectedAudiences || hasExpectedAudience(accessToken, expectedAudiences);

  /**
   * If we have an account but no token, or token is expired, acquire a new one
   */
  if (account && (!accessToken || isTokenExpired(accessToken) || !hasValidAudience)) {
    return from(
      msalService.instance
        .acquireTokenSilent({
          scopes: tokenScopes,
          account: account,
        })
        .then((tokenResponse) => {
          window.localStorage.setItem(tokenStorageKey, tokenResponse.accessToken);
          return req.clone({
            headers: req.headers
              .set('x-access-token', tokenResponse.accessToken)
              .set('username', account.username || ''),
          });
        })
        .catch((error) => {
          console.warn('Failed to get access token:', error);
          // If we have an existing token, use it even if expired
          if (accessToken) {
            return req.clone({
              headers: req.headers.set('x-access-token', accessToken).set('username', account.username || ''),
            });
          }
          // No token available, continue without x-access-token
          return req.clone({
            headers: req.headers.set('username', account.username || ''),
          });
        })
    ).pipe(
      switchMap((clonedReq) => next(clonedReq)),
      finalize(() => {
        loading.set(false, req.url);
      })
    );
  }

  // Token is still valid, use it directly
  let reqClone = req;
  if (accessToken && hasValidAudience) {
    reqClone = req.clone({
      headers: req.headers.set('x-access-token', accessToken).set('username', account?.username || ''),
    });
  }

  return next(reqClone).pipe(
    finalize(() => {
      loading.set(false, req.url);
    })
  );
}

export const httpInterceptorProviders = [{ provide: HTTP_INTERCEPTORS, useClass: HttpRequestInterceptor, multi: true }];
